import React, { useState, useEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import { ListChecks, AlertTriangle, ChevronLeft, Edit } from "lucide-react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import io from "socket.io-client";
import "./ClanEventGameTasksTab.css";

const getSocket = () => {
  return io(process.env.REACT_APP_API_BASE_URL);
};
const socket = getSocket();

const API_BASE_URL = `${process.env.REACT_APP_API_BASE_URL}/api`;
const DIFFICULTIES = ["EASY", "MEDIUM", "HARD", "ELITE", "MASTER"];
const MESSAGE_TIMEOUT_MS = 5000;
const MESSAGE_FADE_DURATION_MS = 1000;

const getAssignedTaskIds = (boardTasks) => {
  return new Set(boardTasks.map((t) => t.task_id).filter((id) => id));
};

const isFixedSquare = (task) => {
  return (
    task.description === "Start" ||
    task.description === "Finish" ||
    task.description === "Ladder" ||
    task.description === "Snake"
  );
};

const TaskItem = ({ task, listId, onClick }) => {
  const isBoard = listId.startsWith("board-");
  const difficultyClass = task.difficulty
    ? task.difficulty.toLowerCase()
    : "blank";
  const listType = isBoard ? "board" : "pool";
  const taskDescription = task.description || "No Task Assigned";
  const taskDifficulty = task.difficulty || "BLANK";
  const fixedSquare = isFixedSquare(task);
  const isEditable = isBoard && !fixedSquare;

  return (
    <div
      className={`task-card ${listType}-task-card task-difficulty-${difficultyClass} ${
        fixedSquare ? "task-card-fixed" : ""
      }`}
    >
      <div className="task-header">
        {isBoard && (
          <span className="square-index-label">SQ {task.square_index}</span>
        )}
        {!fixedSquare && (
          <span className="task-difficulty-label">{taskDifficulty}</span>
        )}
        {isEditable && (
          <span className="edit-icon-container" onClick={() => onClick(task)}>
            <Edit size={14} />
          </span>
        )}
      </div>
      <div className="task-body">{taskDescription}</div>
    </div>
  );
};

function ClanEventGameTasksTab({ clanId, event, gameId, goBackToList }) {
  const [boardTasks, setBoardTasks] = useState([]);
  const [taskPool, setTaskPool] = useState([]);
  const [loading, setLoading] = useState(false);

  const [apiMessage, setApiMessage] = useState(null);
  const [isMessageFading, setIsMessageFading] = useState(false);

  const [isAtTop, setIsAtTop] = useState(true);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSquare, setEditingSquare] = useState(null);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [availableModalTasks, setAvailableModalTasks] = useState([]);

  const fetchTasks = useCallback(
    async (currentTasks = null) => {
      const fetchOptions = { credentials: "include" };

      if (!gameId) {
        setApiMessage({ type: "error", text: "Game ID is missing." });
        setLoading(false);
        return;
      }

      if (!currentTasks) {
        setLoading(true);
        setApiMessage(null);
        setIsMessageFading(false);
      }

      const boardUrl = `${API_BASE_URL}/clan/${clanId}/events/${event.id}/game/${gameId}/tasks`;
      const poolUrl = `${API_BASE_URL}/clan/${clanId}/tasks/`;

      try {
        const [boardResponse, poolResponse] = await Promise.all([
          fetch(boardUrl, fetchOptions),
          fetch(poolUrl, fetchOptions),
        ]);

        if (!boardResponse.ok)
          throw new Error(
            `Failed to fetch board tasks: ${boardResponse.statusText}`
          );
        if (!poolResponse.ok)
          throw new Error(
            `Failed to fetch task pool: ${poolResponse.statusText}`
          );

        const boardData = await boardResponse.json();
        const poolData = await poolResponse.json();

        const fetchedBoardTasks = (boardData.squares || [])
          .map((s) => ({
            id: s.id,
            square_index: s.square_index,
            task_id: s.task_id,
            description: s.task_description,
            difficulty: s.task_difficulty,
          }))
          .sort((a, b) => a.square_index - b.square_index);

        const assignedTaskIds = getAssignedTaskIds(fetchedBoardTasks);

        const availableTaskPool = (poolData.tasks || [])
          .filter((task) => !assignedTaskIds.has(task.id))
          .sort((a, b) => a.difficulty.localeCompare(b.difficulty));

        setBoardTasks(fetchedBoardTasks);
        setTaskPool(availableTaskPool);
      } catch (error) {
        console.error(error);
        setApiMessage({
          type: "error",
          text: `Error loading data: ${error.message}`,
        });
      } finally {
        if (!currentTasks) {
          setLoading(false);
        }
      }
    },
    [clanId, event.id, gameId]
  );

  const saveBoardOrder = useCallback(
    async (updatedTasks) => {
      setLoading(true);
      setApiMessage(null);
      setIsMessageFading(false);

      const reorderUrl = `${API_BASE_URL}/clan/${clanId}/events/${event.id}/game/${gameId}/tasks/reorder`;

      const payload = updatedTasks.map((t) => ({
        id: t.id,
        square_index: t.square_index,
      }));

      const fetchOptions = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ squares: payload }),
        credentials: "include",
      };

      try {
        const response = await fetch(reorderUrl, fetchOptions);
        const data = await response.json();

        if (!response.ok) {
          throw new Error(
            data.error || `Failed to save board order: ${response.statusText}`
          );
        }

        await fetchTasks(updatedTasks);

        setApiMessage({
          type: "success",
          text: "Board order updated and saved successfully!",
        });
      } catch (error) {
        console.error("Error saving board order:", error);
        setApiMessage({
          type: "error",
          text: `Error saving board order: ${error.message}`,
        });
        await fetchTasks();
      } finally {
        setLoading(false);
      }
    },
    [clanId, event.id, gameId, fetchTasks]
  );

  const handleDragSwap = useCallback(
    async (square1Id, square2Id) => {
      setLoading(true);
      setApiMessage(null);
      setIsMessageFading(false);

      const swapUrl = `${API_BASE_URL}/clan/${clanId}/events/${event.id}/game/${gameId}/tasks/swap`;

      const payload = {
        square1_id: square1Id,
        square2_id: square2Id,
      };

      const fetchOptions = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
        credentials: "include",
      };

      try {
        const response = await fetch(swapUrl, fetchOptions);
        const data = await response.json();

        if (!response.ok) {
          throw new Error(
            data.error || `Failed to swap tasks: ${response.statusText}`
          );
        }

        await fetchTasks();

        setApiMessage({
          type: "success",
          text: "Tasks swapped successfully!",
        });
      } catch (error) {
        console.error("Error saving task swap:", error);
        setApiMessage({
          type: "error",
          text: `Error saving task swap: ${error.message}`,
        });
        await fetchTasks();
      } finally {
        setLoading(false);
      }
    },
    [clanId, event.id, gameId, fetchTasks]
  );

  const handleModalSwapTask = async () => {
    if (!editingSquare || !selectedTaskId) return;

    const selectedTask = availableModalTasks.find(
      (t) => t.id === parseInt(selectedTaskId)
    );

    setLoading(true);
    setApiMessage(null);
    setIsMessageFading(false);

    const updateUrl = `${API_BASE_URL}/clan/${clanId}/events/${event.id}/game/${gameId}/tasks/${editingSquare.id}`;
    const fetchOptions = {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        task_id: selectedTask.id,
        description: selectedTask.description,
        difficulty: selectedTask.difficulty,
      }),
      credentials: "include",
    };

    try {
      const response = await fetch(updateUrl, fetchOptions);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.error || `Failed to swap task: ${response.statusText}`
        );
      }

      closeModal();
      await fetchTasks();

      socket.emit("tasks_reordered", {
        game_id: gameId,
        message: `Square ${editingSquare.square_index} task updated.`,
      });

      setApiMessage({
        type: "success",
        text: `Task on Square ${editingSquare.square_index} assigned successfully!`,
      });
    } catch (error) {
      console.error("Error saving task swap:", error);
      setApiMessage({
        type: "error",
        text: `Error saving task swap: ${error.message}`,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  useEffect(() => {
    if (!gameId) return;

    socket.emit("join_game", { game_id: gameId });

    const handleTasksUpdated = (data) => {
      if (data.board_data && data.board_data.board_squares) {
        fetchTasks();
      }
    };

    socket.on("tasks_reordered", handleTasksUpdated);

    return () => {
      socket.emit("leave_game", { game_id: gameId });
      socket.off("tasks_reordered", handleTasksUpdated);
    };
  }, [gameId, fetchTasks]);

  useEffect(() => {
    const handleWindowScroll = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      setIsAtTop(scrollTop < 20);
    };

    window.addEventListener("scroll", handleWindowScroll);

    return () => {
      window.removeEventListener("scroll", handleWindowScroll);
    };
  }, []);

  useEffect(() => {
    let hideTimer;
    let fadeTimer;

    if (apiMessage) {
      hideTimer = setTimeout(() => {
        setIsMessageFading(true);

        fadeTimer = setTimeout(() => {
          setApiMessage(null);
          setIsMessageFading(false);
        }, MESSAGE_FADE_DURATION_MS);
      }, MESSAGE_TIMEOUT_MS);
    }

    return () => {
      clearTimeout(hideTimer);
      clearTimeout(fadeTimer);
    };
  }, [apiMessage]);

  const fetchAvailableTasksForSquare = useCallback(
    async (squareId) => {
      const fetchOptions = { credentials: "include" };
      const availableUrl = `${API_BASE_URL}/clan/${clanId}/events/${event.id}/game/${gameId}/tasks/${squareId}/available`;

      try {
        const response = await fetch(availableUrl, fetchOptions);
        if (!response.ok) {
          throw new Error(
            `Failed to fetch available tasks: ${response.statusText}`
          );
        }
        const data = await response.json();
        return data.available_tasks || [];
      } catch (error) {
        console.error("Error fetching available tasks:", error);
        setApiMessage({
          type: "error",
          text: `Failed to fetch swap options: ${error.message}`,
        });
        return [];
      }
    },
    [clanId, event.id, gameId]
  );

  const openEditModal = useCallback(
    async (task) => {
      if (isFixedSquare(task)) {
        return;
      }

      const currentDifficulty = task.difficulty || "BLANK";
      const isBlankSquare = currentDifficulty === "BLANK";

      if (isBlankSquare) {
        setEditingSquare(task);
        setIsModalOpen(true);
        return;
      }

      setLoading(true);
      setEditingSquare(task);

      setSelectedTaskId("");

      const tasks = await fetchAvailableTasksForSquare(task.id);
      setAvailableModalTasks(tasks);

      setLoading(false);
      setIsModalOpen(true);
    },
    [fetchAvailableTasksForSquare]
  );

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingSquare(null);
    setSelectedTaskId("");
    setAvailableModalTasks([]);
    setApiMessage(null);
    setIsMessageFading(false);
  };

  const getTasksByDifficulty = (tasks) => {
    const categorized = {
      start: [],
      finish: [],
      unassigned: [],
    };
    DIFFICULTIES.forEach((d) => {
      categorized[d.toLowerCase()] = [];
    });

    tasks.forEach((task) => {
      if (task.description === "Start") {
        categorized.start.push(task);
      } else if (task.description === "Finish") {
        categorized.finish.push(task);
      } else if (task.difficulty && DIFFICULTIES.includes(task.difficulty)) {
        categorized[task.difficulty.toLowerCase()].push(task);
      } else {
        categorized.unassigned.push(task);
      }
    });

    return categorized;
  };

  const onDragEnd = useCallback(
    (result) => {
      const { source, destination } = result;

      setApiMessage(null);
      setIsMessageFading(false);

      if (!destination) {
        return;
      }

      if (source.droppableId !== destination.droppableId) {
        setApiMessage({
          type: "error",
          text: "You can only re-order squares within their own Difficulty Tier column. Drag-and-swap is not supported across columns.",
        });
        return;
      }

      const categorizedTasks = getTasksByDifficulty(boardTasks);
      const difficultyKey = source.droppableId.replace("board-", "");
      const sourceList = categorizedTasks[difficultyKey];

      const sourceTask = sourceList[source.index];
      const destinationTask = sourceList[destination.index];

      if (isFixedSquare(sourceTask) || isFixedSquare(destinationTask)) {
        setApiMessage({
          type: "error",
          text: "Cannot swap tasks with Start, Finish, Ladder, or Snake squares.",
        });
        return;
      }

      if (source.index === destination.index) {
        return;
      }

      const newSourceList = Array.from(sourceList);

      newSourceList[destination.index] = sourceTask;
      newSourceList[source.index] = destinationTask;

      const reorderedTasks = [
        ...categorizedTasks.start,
        ...categorizedTasks.unassigned,
        ...categorizedTasks.easy,
        ...categorizedTasks.medium,
        ...categorizedTasks.hard,
        ...categorizedTasks.elite,
        ...categorizedTasks.master,
        ...categorizedTasks.finish,
      ].filter((t) => t);

      const startIndex = reorderedTasks.findIndex(
        (t) => t.id === sourceList[0].id
      );
      if (startIndex !== -1) {
        reorderedTasks.splice(startIndex, sourceList.length, ...newSourceList);
      } else {
        return;
      }

      const updatedTasks = reorderedTasks.map((task, index) => ({
        ...task,
        square_index: index + 1,
      }));

      setBoardTasks(updatedTasks);
      handleDragSwap(sourceTask.id, destinationTask.id);
    },
    [boardTasks, handleDragSwap]
  );

  const showFixedMessage = apiMessage && !isAtTop;
  const showInlineMessage = apiMessage && isAtTop;

  const portalMessageClassName = `api-message ${
    apiMessage ? apiMessage.type : ""
  } ${showFixedMessage ? "" : "api-message-hidden"} ${
    isMessageFading ? "fading-out" : ""
  }`;

  const categorizedTasks = getTasksByDifficulty(boardTasks);

  const renderEditModal = () => {
    if (!isModalOpen || !editingSquare) return null;

    const currentDifficulty = editingSquare.difficulty || "BLANK";
    const filteredTasks = availableModalTasks;
    const isBlankSquare = currentDifficulty === "BLANK";
    const currentTaskDescription =
      editingSquare.description || "No Task Assigned";
    const isButtonDisabled = loading || !selectedTaskId;
    const hasAvailableOptions = filteredTasks.length > 0;

    if (isBlankSquare) {
      return (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Cannot Assign Task</h3>
            <p>
              This square (SQ {editingSquare.square_index}) has no defined
              difficulty. Only squares with a specific difficulty tier (EASY,
              MEDIUM, etc.) can have tasks assigned.
            </p>
            <div className="modal-actions">
              <button
                onClick={closeModal}
                className="btn-secondary"
                disabled={loading}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="modal-overlay">
        <div className="modal-content">
          <h3>Swap Task for Square {editingSquare.square_index}</h3>
          <p>
            <strong>Required Difficulty Tier:</strong>{" "}
            <strong>{currentDifficulty}</strong>
          </p>
          <div className="current-task-display-box">
            <p style={{ margin: 0 }}>
              <strong>Current Task:</strong>
            </p>
            <p style={{ marginTop: "5px", fontWeight: "bold" }}>
              {currentTaskDescription}
            </p>
          </div>
          <div className="modal-form-group">
            <label htmlFor="task-select">Select a new task to assign:</label>
            <select
              id="task-select"
              value={selectedTaskId}
              onChange={(e) => setSelectedTaskId(e.target.value)}
              disabled={loading || !hasAvailableOptions}
            >
              <option value="" disabled={true}>
                -- Select a Task --
              </option>
              {filteredTasks.map((task) => (
                <option key={task.id} value={task.id}>
                  {task.description}
                </option>
              ))}
            </select>
            {!hasAvailableOptions && (
              <p
                className="no-tasks-warning"
                style={{ color: "orange", marginTop: "5px" }}
              >
                <AlertTriangle
                  size={14}
                  style={{ marginRight: "5px", verticalAlign: "bottom" }}
                />
                No unassigned tasks of difficulty '{currentDifficulty}' are
                available.
              </p>
            )}
          </div>
          {loading && <p className="loading-message">Loading tasks...</p>}
          <div className="modal-actions">
            <button
              onClick={handleModalSwapTask}
              className="btn-primary"
              disabled={isButtonDisabled}
            >
              {loading ? "Saving..." : "Assign & Save"}
            </button>
            <button
              onClick={closeModal}
              className="btn-secondary"
              disabled={loading}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderFixedApiMessagePortal = () => {
    if (!apiMessage) return null;

    return ReactDOM.createPortal(
      <div className={portalMessageClassName}>
        {apiMessage.type === "error" && (
          <AlertTriangle size={20} style={{ marginRight: "10px" }} />
        )}
        {apiMessage.text}
      </div>,
      document.body
    );
  };

  const renderDifficultyColumn = (difficultyKey, tasks) => {
    const listId = `board-${difficultyKey.toLowerCase()}`;
    const difficultyTitle =
      difficultyKey.charAt(0).toUpperCase() +
      difficultyKey.slice(1).toLowerCase();

    return (
      <div key={listId} className="board-tasks-column">
        <h4
          style={{ display: "flex", alignItems: "center", marginBottom: "5px" }}
        >
          <span
            className={`task-difficulty-label task-difficulty-${difficultyKey.toLowerCase()}`}
          >
            {difficultyTitle}
          </span>
          <span
            style={{
              marginLeft: "10px",
              fontWeight: "normal",
              fontSize: "0.8em",
              color: "#777",
            }}
          >
            ({tasks.length} Squares)
          </span>
        </h4>
        <Droppable droppableId={listId}>
          {(provided, snapshot) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              data-list-id={listId}
              className={`task-list-container board-list ${
                snapshot.isDraggingOver ? "is-drag-over" : ""
              }`}
            >
              {tasks.map((task, index) => (
                <Draggable
                  key={task.id}
                  draggableId={`board-${task.id}`}
                  index={index}
                  isDragDisabled={
                    isFixedSquare(task) ||
                    !task.difficulty ||
                    task.difficulty === "BLANK"
                  }
                >
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...(!isFixedSquare(task)
                        ? provided.dragHandleProps
                        : {})}
                      className={`draggable-item ${
                        snapshot.isDragging ? "is-dragging" : ""
                      }`}
                    >
                      <TaskItem
                        task={task}
                        listId={listId}
                        onClick={openEditModal}
                      />
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
              {tasks.length === 0 && !loading && (
                <p
                  style={{
                    textAlign: "center",
                    color: "#999",
                    padding: "20px",
                  }}
                >
                  No squares assigned to this difficulty.
                </p>
              )}
            </div>
          )}
        </Droppable>
      </div>
    );
  };

  const renderFixedTasks = (tasks, title, listId) => {
    if (tasks.length === 0) return null;

    return (
      <div key={listId} className="board-tasks-column">
        <h4 style={{ display: "flex", alignItems: "center" }}>
          <span style={{ marginRight: "10px", fontWeight: "bold" }}>
            {title}
          </span>
          <span
            style={{ fontWeight: "normal", fontSize: "0.8em", color: "#777" }}
          >
            ({tasks.length} Squares)
          </span>
        </h4>
        <div className="task-list-container board-list">
          {tasks.map((task) => (
            <div key={task.id}>
              <TaskItem task={task} listId={listId} onClick={openEditModal} />
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="clan-event-tasks-tab clan-event-game-tab-container">
      {renderEditModal()}
      {renderFixedApiMessagePortal()}

      <div className="event-sub-nav">
        <button className="sub-nav-button" onClick={goBackToList}>
          <ChevronLeft size={18} style={{ marginRight: "5px" }} /> Back to Events
        </button>
        <span className="current-context-display">
          Managing:&nbsp;
          <strong className="current-view-name">{"Game Tasks"}</strong>
        </span>
      </div>

      <div className="game-tab-content-area">
        {showInlineMessage && (
          <div
            className={`inline-api-message ${apiMessage.type} ${
              isMessageFading ? "fading-out" : ""
            }`}
          >
            {apiMessage.type === "error" && (
              <AlertTriangle size={20} style={{ marginRight: "10px" }} />
            )}
            {apiMessage.text}
          </div>
        )}

        <p className="tab-instruction-note">
          The board is now organized by <strong>Difficulty Tier</strong>. You
          can
          <strong> drag-and-swap</strong> squares <em>within</em> their
          respective difficulty list. The square you drop under will swap
          positions with the square you dragged.&nbsp;
          <strong>The new order is saved automatically.</strong> Click the&nbsp;
          <strong style={{ whiteSpace: "nowrap" }}>
            <Edit size={14} style={{ verticalAlign: "middle" }} /> Edit
          </strong>
          &nbsp; icon to <strong>swap</strong> a square's assigned task via the
          modal.
          <strong> Start</strong>, <strong>Finish</strong>,&nbsp;
          <strong>Ladder</strong>, and <strong>Snake</strong>&nbsp; squares
          cannot be moved or swapped.
        </p>

        <DragDropContext onDragEnd={onDragEnd}>
          <div className="task-management-layout">
            {renderFixedTasks(
              categorizedTasks.start,
              "Start Square",
              "board-start"
            )}

            {DIFFICULTIES.map((difficulty) =>
              renderDifficultyColumn(
                difficulty,
                categorizedTasks[difficulty.toLowerCase()]
              )
            )}

            {renderFixedTasks(
              categorizedTasks.unassigned,
              "Unassigned/Blank Squares",
              "board-unassigned"
            )}

            {renderFixedTasks(
              categorizedTasks.finish,
              "Finish Square",
              "board-finish"
            )}
          </div>
        </DragDropContext>
      </div>
    </div>
  );
}

export default ClanEventGameTasksTab;
